import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "../api/client";
import { useDomainStore } from "../stores/domainStore";
import { Button } from "../components/ui/Button";
import { Spinner } from "../components/ui/Spinner";

function ArrowLeftIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  );
}

function DownloadIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function ChevronLeftIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
  );
}

function ChevronRightIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  );
}

export default function DocumentViewPage() {
  const { id: domainId, docId } = useParams();
  const { domains } = useDomainStore();
  const domain = domains.find((d) => d.id === domainId);

  const [doc, setDoc] = useState(null);
  const [chunks, setChunks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [showOcr, setShowOcr] = useState(true);

  useEffect(() => {
    loadDocument();
  }, [docId]);

  async function loadDocument() {
    setLoading(true);
    setError(null);
    try {
      const { data } = await client.get(`/api/documents/${docId}`);
      setDoc(data.document);
      setChunks(data.chunks || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  const fileUrl = doc?.source_path
    ? `${import.meta.env.VITE_API_BASE_URL || "https://api.digitize.mr.jandayan.net"}/${doc.source_path}`
    : null;
  const isPdf = doc?.mime_type === "application/pdf";
  const isImage = doc?.mime_type?.startsWith("image/");

  const totalChunks = chunks.length;
  const currentChunkData = chunks[currentChunk] || null;

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col -m-6">
      {/* Header toolbar */}
      <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to={`/domains/${domainId}/documents`}>
            <Button size="sm" variant="ghost">
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Back
            </Button>
          </Link>
          <div className="h-5 w-px bg-slate-200 dark:bg-slate-700" />
          <h1 className="text-sm font-semibold truncate max-w-md">
            {doc?.title || "Loading..."}
          </h1>
          {doc?.page_count && (
            <span className="text-xs text-slate-500">{doc.page_count} pages</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={showOcr ? "secondary" : "ghost"}
            onClick={() => setShowOcr(!showOcr)}
          >
            {showOcr ? "Hide OCR" : "Show OCR"}
          </Button>
          {fileUrl && (
            <a href={fileUrl} download target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline">
                <DownloadIcon className="h-4 w-4 mr-1" />
                Download
              </Button>
            </a>
          )}
        </div>
      </div>

      {/* Main split view */}
      <div className="flex-1 flex overflow-hidden">
        {/* Original file */}
        <div className={`${showOcr ? 'w-1/2' : 'w-full'} border-r border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-950 overflow-auto`}>
          {loading && (
            <div className="flex items-center justify-center h-full">
              <Spinner className="h-6 w-6" />
            </div>
          )}
          {!loading && error && (
            <div className="flex items-center justify-center h-full text-red-500">{error}</div>
          )}
          {!loading && !error && fileUrl && isPdf && (
            <iframe
              src={fileUrl}
              className="w-full h-full border-0"
              title="PDF viewer"
            />
          )}
          {!loading && !error && fileUrl && isImage && (
            <img
              src={fileUrl}
              alt={doc?.title}
              className="max-w-full h-auto mx-auto"
            />
          )}
          {!loading && !error && !fileUrl && (
            <div className="flex items-center justify-center h-full text-slate-500">
              No preview available
            </div>
          )}
        </div>

        {/* OCR panel */}
        {showOcr && (
          <div className="w-1/2 flex flex-col bg-white dark:bg-slate-900 overflow-hidden">
            {/* Chunk navigation */}
            {totalChunks > 1 && (
              <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 px-4 py-2 flex items-center justify-between bg-slate-50 dark:bg-slate-800/50">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={currentChunk === 0}
                  onClick={() => setCurrentChunk((c) => Math.max(0, c - 1))}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                <span className="text-xs text-slate-600 dark:text-slate-400">
                  Chunk {currentChunk + 1} of {totalChunks}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={currentChunk >= totalChunks - 1}
                  onClick={() => setCurrentChunk((c) => Math.min(totalChunks - 1, c + 1))}
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Chunk content */}
            <div className="flex-1 overflow-auto p-4">
              {loading && (
                <div className="space-y-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="h-3 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                  ))}
                </div>
              )}
              {!loading && currentChunkData && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500">
                      Chunk {currentChunk + 1} — Page {currentChunkData.page_number || "?"}
                    </span>
                    <span className="text-xs text-slate-400">
                      {currentChunkData.content?.length || 0} chars
                    </span>
                  </div>
                  <pre className="text-sm text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-wrap font-mono">
                    {currentChunkData.content}
                  </pre>
                </div>
              )}
              {!loading && totalChunks === 0 && (
                <p className="text-sm text-slate-500">No OCR text available for this document.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
